import {
	buildContentSearchPath,
	parseContentIds,
	serializeContentIds,
} from './content-id-utils.mjs';

const { Button, Spinner, TextControl } = wp.components;
const { useEffect, useMemo, useState } = wp.element;
const { __, sprintf } = wp.i18n;

function resultLabel(item) {
	const title = item?.title?.trim();
	const subtype = item?.subtype?.trim();

	return sprintf(
		/* translators: 1: content title or fallback label, 2: post type, 3: post ID. */
		__('%1$s · %2$s · #%3$d', 'simple-sitemap'),
		title || __('(no title)', 'simple-sitemap'),
		subtype || __('content', 'simple-sitemap'),
		item.id
	);
}

/**
 * Search, add, remove, and manually edit a block attribute containing IDs.
 *
 * The attribute remains a comma-separated string so existing blocks and the
 * server-side renderer retain their established data contract.
 *
 * @param {Object}   props             Component properties.
 * @param {string}   props.label       Control label.
 * @param {string}   props.help        Manual-field help text.
 * @param {string}   props.value       Stored comma-separated IDs.
 * @param {string[]} props.postTypes   Post types selected by the block.
 * @param {Function} props.onChange    Attribute update callback.
 * @param {boolean}  [props.multiple]  Allow more than one selected item.
 * @param {string}   [props.manualLabel] Manual input label.
 * @return {Element} Content ID picker.
 */
export function ContentIdPicker({
	label,
	help,
	value = '',
	postTypes = [],
	onChange,
	multiple = true,
	manualLabel = __('Manual ID list', 'simple-sitemap'),
}) {
	const selectedIds = useMemo(() => parseContentIds(value), [value]);
	const selectedKey = selectedIds.join(',');
	const postTypesKey = postTypes.join(',');
	const [itemsById, setItemsById] = useState({});
	const [search, setSearch] = useState('');
	const [results, setResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	useEffect(() => {
		if (!selectedKey || !postTypesKey) {
			return undefined;
		}

		let active = true;
		wp.apiFetch({
			path: buildContentSearchPath({
				include: selectedIds,
				postTypes,
				perPage: selectedIds.length,
			}),
			method: 'GET',
		}).then(
			(items) => {
				if (!active) {
					return;
				}

				setItemsById((knownItems) => ({
					...knownItems,
					...Object.fromEntries(
						(items || []).map((item) => [item.id, item])
					),
				}));
			},
			() => {
				// Raw IDs remain fully editable if a title cannot be resolved.
			}
		);

		return () => {
			active = false;
		};
	}, [postTypesKey, selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const searchTerm = search.trim();
		if (searchTerm.length < 2 || !postTypesKey) {
			setResults([]);
			setLoading(false);
			setError('');
			return undefined;
		}

		let active = true;
		setResults([]);
		setLoading(true);
		setError('');
		const timeout = window.setTimeout(() => {
			wp.apiFetch({
				path: buildContentSearchPath({
					search: searchTerm,
					exclude: selectedIds,
					postTypes,
				}),
				method: 'GET',
			}).then(
				(items) => {
					if (active) {
						setResults(Array.isArray(items) ? items : []);
						setLoading(false);
					}
				},
				() => {
					if (active) {
						setResults([]);
						setLoading(false);
						setError(
							__(
								'Unable to search content. You can still enter IDs manually.',
								'simple-sitemap'
							)
						);
					}
				}
			);
		}, 300);

		return () => {
			active = false;
			window.clearTimeout(timeout);
		};
	}, [postTypesKey, search, selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

	const updateIds = (ids) => onChange(serializeContentIds(ids));
	const addItem = (item) => {
		setItemsById((knownItems) => ({ ...knownItems, [item.id]: item }));
		updateIds(multiple ? [...selectedIds, item.id] : [item.id]);
		setSearch('');
		setResults([]);
	};

	return (
		<div
			className="simple-sitemap-content-picker"
			role="group"
			aria-label={label}
		>
			<h3>{label}</h3>
			{selectedIds.length > 0 && (
				<ul className="simple-sitemap-content-picker__selected">
					{selectedIds.map((id) => {
						const item = itemsById[id];
						const itemName = item
							? resultLabel(item)
							: sprintf(
									/* translators: %d: post or page ID. */
									__('Content #%d', 'simple-sitemap'),
									id
								);

						return (
							<li key={id}>
								<span>{itemName}</span>
								<Button
									variant="tertiary"
									isDestructive
									onClick={() =>
										updateIds(
											selectedIds.filter(
												(selectedId) =>
													selectedId !== id
											)
										)
									}
									aria-label={sprintf(
										/* translators: %s: selected content label. */
										__('Remove %s', 'simple-sitemap'),
										itemName
									)}
								>
									{__('Remove', 'simple-sitemap')}
								</Button>
							</li>
						);
					})}
				</ul>
			)}
			<TextControl
				label={__('Search content', 'simple-sitemap')}
				help={
					postTypesKey
						? __('Enter at least two characters.', 'simple-sitemap')
						: __(
								'Select one or more post types before searching.',
								'simple-sitemap'
							)
				}
				value={search}
				disabled={!postTypesKey}
				onChange={setSearch}
			/>
			<div
				className="simple-sitemap-content-picker__status"
				aria-live="polite"
			>
				{loading && <Spinner />}
				{error && (
					<p className="simple-sitemap-control-error">{error}</p>
				)}
				{!loading && search.trim().length >= 2 && !error && (
					<p>
						{results.length > 0
							? sprintf(
									/* translators: %d: number of search results. */
									__(
										'%d matching items found.',
										'simple-sitemap'
									),
									results.length
								)
							: __(
									'No matching content found.',
									'simple-sitemap'
								)}
					</p>
				)}
			</div>
			{results.length > 0 && (
				<ul className="simple-sitemap-content-picker__results">
					{results.map((item) => (
						<li key={item.id}>
							<Button
								variant="secondary"
								onClick={() => addItem(item)}
							>
								{sprintf(
									/* translators: %s: search result label. */
									__('Add %s', 'simple-sitemap'),
									resultLabel(item)
								)}
							</Button>
						</li>
					))}
				</ul>
			)}
			<TextControl
				label={manualLabel}
				help={help}
				value={value}
				onChange={onChange}
			/>
		</div>
	);
}
